import { isDefined, isString } from '@sgme/fp';
import { thunksByWorkspaceAndDoc } from 'epic/streamingQuote/utils';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { ofType } from 'redux-observable';
import { EMPTY, Observable, of, race, type SchedulerLike } from 'rxjs';
import { delay, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Action } from 'state/actions';
import { dynamicStreamTradeAuthReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { getWorkspaceFromQuoteId } from 'state/quotes/selectors';
import {
  streamingQuoteClosed,
  streamingQuoteExpired,
  streamingQuoteRejected,
} from 'state/quotes/streaming/streaming.actions';
import { streamingPriceReceivedThunk } from 'state/quotes/streaming/streaming.thunk';
import type { EpicWithThunk } from 'state/state.common';
import { staticStreamTradeAuthReceivedThunk } from 'state/static/staticForm.thunks';
import { workspaceTabUnfrozen } from 'state/workspaces/workspace.actions';

export function priceStreamingClosedEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('STREAMING_QUOTE_ACKED'),
      mergeMap(({ tabId, quoteId, workspace }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        return streaming.priceStreamingMessage$.pipe(
          ofType('CloseQuotePrice'),
          filter(x => x.quoteId === quoteId),
          mergeMap(() => {
            return of(streamingQuoteClosed(tabId, quoteId, workspace), workspaceTabUnfrozen(tabId, workspace, false));
          }),
          takeUntil(race(tradeRequested(action$, quoteId), quoteOrTabRemoved$, streamingExpired(action$, quoteId))),
        );
      }),
    );
}
export function priceStreamingRejectedEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('STREAMING_QUOTE_ACKED'),
      mergeMap(({ tabId, quoteId, workspace }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        return streaming.priceStreamingMessage$.pipe(
          ofType('RejectedQuotePrice'),
          filter(x => x.quoteId === quoteId),
          mergeMap(({ error }) => {
            return of(streamingQuoteRejected(tabId, quoteId, error), workspaceTabUnfrozen(tabId, workspace, false));
          }),
          takeUntil(race(tradeRequested(action$, quoteId), quoteOrTabRemoved$, streamingExpired(action$, quoteId))),
        );
      }),
    );
}

export function priceStreamingExpiredEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$) =>
    action$.pipe(
      ofType('STREAMING_QUOTE_ACKED'),
      mergeMap(({ tabId, quoteId, workspace, estimatedQuotingDurationInMillis }) => {
        return of(streamingQuoteExpired(tabId, quoteId, workspace), workspaceTabUnfrozen(tabId, workspace, false)).pipe(
          delay(estimatedQuotingDurationInMillis, appScheduler),
          takeUntil(race(tradeRequested(action$, quoteId), quoteOrTabRemoved(action$, tabId, quoteId, state$))),
        );
      }),
    );
}
export function priceStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('STREAMING_QUOTE_ACKED'),
      mergeMap(({ tabId, quoteId, workspace }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

        return streaming.priceStreamingMessage$.pipe(
          ofType('NextQuotePrice'),
          filter(x => x.quoteId === quoteId),
          map(priceEvent => {
            return streamingPriceReceivedThunk(
              tabId,
              {
                ...priceEvent,
                remainingValues: isString(priceEvent.remainingValues) ? JSON.parse(priceEvent.remainingValues) : {},
              },
              workspace,
            );
          }),
          takeUntil(
            race(
              quoteOrTabRemoved$,
              tradeRequested(action$, quoteId),
              streamingExpired(action$, quoteId),
              streamingRejected(action$, quoteId),
              streamingClosed(action$, quoteId),
              quoteStale(action$, quoteId),
            ),
          ),
        );
      }),
    );
}
export function tradeAuthStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('STREAMING_QUOTE_ACKED'),
      mergeMap(({ tabId, quoteId, workspace }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

        return streaming.priceStreamingMessage$.pipe(
          ofType('NexTradeAuth'),
          filter(x => x.quoteId === quoteId),
          map(() => {
            return workspace === 'dynamic'
              ? dynamicStreamTradeAuthReceivedThunk(tabId, quoteId)
              : staticStreamTradeAuthReceivedThunk(tabId, quoteId);
          }),
          takeUntil(
            race(
              quoteOrTabRemoved$,
              streamingExpired(action$, quoteId),
              streamingRejected(action$, quoteId),
              streamingClosed(action$, quoteId),
              quoteStale(action$, quoteId),
            ),
          ),
        );
      }),
    );
}
export function documentationStreamingEpic(): EpicWithThunk {
  return (action$, state$, { streaming }) =>
    action$.pipe(
      ofType('STREAMING_QUOTE_ACKED'),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteId }, state]) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        const thunks = thunksByWorkspaceAndDoc[getWorkspaceFromQuoteId(state, quoteId)];
        if (!isDefined(thunks)) {
          return EMPTY;
        }
        return streaming.documentationMessage$.pipe(
          ofType('NextDocumentation'),
          filter(x => x.quoteId === quoteId),
          mergeMap(doc => {
            const { url, docType, ...remainingDocument } = doc;
            return of(
              thunks[docType]?.(tabId, quoteId, {
                quoteId,
                requestId: quoteId,
                listResources: [{ ...remainingDocument, uri: url }],
              }),
            );
          }),
          takeUntil(
            race(
              quoteOrTabRemoved$,
              streamingExpired(action$, quoteId),
              streamingRejected(action$, quoteId),
              streamingClosed(action$, quoteId),
              quoteStale(action$, quoteId),
            ),
          ),
        );
      }),
    );
}
function streamingExpired(action$: Observable<Action>, quoteId: string) {
  return action$.pipe(
    ofType('STREAMING_QUOTE_EXPIRED'),
    filter(a => a.quoteId === quoteId),
  );
}
function quoteStale(action$: Observable<Action>, quoteId: string) {
  return action$.pipe(
    ofType('QUOTE_STALE'),
    filter(a => a.quoteId === quoteId),
  );
}
function streamingClosed(action$: Observable<Action>, quoteId: string) {
  return action$.pipe(
    ofType('STREAMING_QUOTE_CLOSED'),
    filter(a => a.quoteId === quoteId),
  );
}
function streamingRejected(action$: Observable<Action>, quoteId: string) {
  return action$.pipe(
    ofType('STREAMING_QUOTE_REJECTED'),
    filter(a => a.quoteId === quoteId),
  );
}
function tradeRequested(action$: Observable<Action>, quoteId: string) {
  return action$.pipe(
    ofType('STREAMING_TRADE_REQUESTED'),
    filter(a => a.quoteId === quoteId),
  );
}
